import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import supabase from './supabaseClient';
import { fetchProfile } from './components/Profile/ProfileManipulation';
import { getTheme, normalizeBackgroundImage, isBackgroundImageValue } from './components/Functions/getTheme';
import getColors from './components/Functions/getColors';

// Create UserContext
const UserContext = createContext();

const toSolidThemeColor = (value, fallback = 'rgb(3, 15, 64)') => {
  if (!value) return fallback;
  const normalized = String(value).trim();
  const rgbaMatch = normalized.match(/^rgba\(([^)]+)\)$/i);
  if (rgbaMatch) {
    const [r, g, b] = rgbaMatch[1].split(',').map((part) => part.trim()).slice(0, 3);
    return `rgb(${r}, ${g}, ${b})`;
  }
  return normalized;
};

// Helper to resolve theme colors with defaults
const resolveThemeColors = (theme) => {
  const defaultTheme = {
    name: "default",
    image: '#f1ebe0',
    color: 'rgba(3,15,64,1)',
    shadowClass: "background-shadow",
    textClass: "text-gray-700 dark:text-gray-200",
    primary: ["green", 500],
    secondary: ["purple", 500],
    tertiary: ["orange", 500],
    border: ["green", 500],
    shadow: false,
  };

  const resolvedTheme = {
    ...defaultTheme,
    ...(theme || {}),
  };

  return {
    ...resolvedTheme,
    image: normalizeBackgroundImage(resolvedTheme.image),
    primaryColor: getColors(resolvedTheme.primary),
    secondaryColor: getColors(resolvedTheme.secondary),
    tertiaryColor: getColors(resolvedTheme.tertiary),
    borderColor: getColors(resolvedTheme.border),
    textColor: resolvedTheme.textClass,
    shadow: resolvedTheme.shadow,
    color: resolvedTheme.color,
  };
};

// UserProvider component to manage user state globally
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [popupStates, setPopupStates] = useState({});
  const [popupStatesLoaded, setPopupStatesLoaded] = useState(false); // New state
  const [loading, setLoading] = useState(true);
  // Ref so auth listeners always see the current signup flag (state alone is stale in mount-only effects)
  const isSignUpProcessRef = useRef(false);

  // State to track color scheme changes for default theme
  const [colorScheme, setColorScheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  // Memoize the theme calculation
  const theme = useMemo(() => {
    return resolveThemeColors(getTheme(profile?.theme, colorScheme));
  }, [profile?.theme, colorScheme]);

  // Push theme into CSS so pages don't recompute background strings
  useEffect(() => {
    if (!theme) return;
    const solidThemeColor = toSolidThemeColor(theme.color);
    if (theme.image) {
      document.documentElement.style.setProperty('--theme-background', theme.image);
      if (isBackgroundImageValue(theme.image)) {
        document.documentElement.style.setProperty('--theme-background-image', theme.image);
        document.documentElement.style.setProperty('--theme-background-color', solidThemeColor);
      } else {
        document.documentElement.style.setProperty('--theme-background-image', 'none');
        document.documentElement.style.setProperty('--theme-background-color', theme.image);
      }
    }
    if (theme.color) {
      document.documentElement.style.setProperty('--theme-border-color', theme.color);
      document.documentElement.style.setProperty('--theme-surface-color', solidThemeColor);
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', solidThemeColor);
      }
    }
  }, [theme]);

  const fetchProfileWithRetry = async (userId, attempts = 6, delayMs = 250) => {
    for (let i = 0; i < attempts; i++) {
      const userProfile = await fetchProfile(userId);
      if (userProfile) return userProfile;
      // During signup the profile row may lag the auth session by a beat
      if (!isSignUpProcessRef.current && i >= 1) break;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    return null;
  };

  // Fetch the user and profile
  const getUser = async () => {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      console.error("Error getting session:", error);
      setLoading(false);
      return;
    }

    if (session?.user) {
      setUser(session.user);
      const userProfile = await fetchProfileWithRetry(session.user.id);

      if (userProfile) {
        setProfile(userProfile);
        setPopupStates(userProfile.popup_states || {});
        setPopupStatesLoaded(true);
        isSignUpProcessRef.current = false;
      } else if (!isSignUpProcessRef.current) {
        // Broken session (auth user, no profile) — clear quietly without hard reload
        console.warn('No profile found for session user. Signing out.');
        await logout();
      }
    } else {
      setUser(null);
      setProfile(null);
      setPopupStates({});
      setPopupStatesLoaded(false);
      isSignUpProcessRef.current = false;
    }

    setLoading(false);
  };

  // Logout function to clear user state
  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Error logging out:", error);
    } else {
      setUser(null);
      setProfile(null);
      setPopupStates({});
      setPopupStatesLoaded(false);  // Reset on logout
      
      // Reset theme to default
      const defaultTheme = resolveThemeColors(null);
      const solidThemeColor = toSolidThemeColor(defaultTheme.color);
      document.documentElement.style.setProperty('--theme-border-color', defaultTheme.color);
      document.documentElement.style.setProperty('--theme-surface-color', solidThemeColor);
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', solidThemeColor);
      }
    }
  };

  // Update popup state in Supabase and locally
  const updatePopupState = async (popupKey, dismissed) => {
    try {
      const updatedStates = { ...popupStates, [popupKey]: dismissed };
      const { error } = await supabase
        .from('profiles')
        .update({ popup_states: updatedStates })
        .eq('id', user?.id);

      if (error) {
        console.error("Error updating popup states:", error);
      } else {
        setPopupStates(updatedStates);  // Update locally if successful
      }
    } catch (err) {
      console.error("Unexpected error updating popup states:", err);
    }
  };

  // Effect to initialize user on mount and listen for auth changes
  useEffect(() => {
    getUser();

    // Listen for color scheme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleColorSchemeChange = (e) => {
      setColorScheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleColorSchemeChange);

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        // Don't block the auth callback; resolve profile asynchronously with retries
        (async () => {
          setUser(session.user);

          const userProfile = await fetchProfileWithRetry(
            session.user.id,
            isSignUpProcessRef.current ? 8 : 3
          );

          if (userProfile) {
            setProfile(userProfile);
            setPopupStates(userProfile.popup_states || {});
            setPopupStatesLoaded(true);
            isSignUpProcessRef.current = false;
            return;
          }

          // Still no profile. During signup, Welcome may still be inserting — do not sign out.
          if (isSignUpProcessRef.current) {
            return;
          }

          console.warn('No profile found for the given user ID. Signing out.');
          await logout();
        })();
      } else {
        setUser(null);
        setProfile(null);
        setPopupStates({});
        setPopupStatesLoaded(false);
        isSignUpProcessRef.current = false;
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
      mediaQuery.removeEventListener('change', handleColorSchemeChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only auth and color-scheme listeners
  }, []);

  // Function to mark that sign-up process has started
  const startSignUpProcess = () => {
    isSignUpProcessRef.current = true;
  };

  const finishSignUpProcess = () => {
    isSignUpProcessRef.current = false;
  };

  // Function to manually toggle color scheme
  const toggleColorScheme = () => {
    setColorScheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  // Subscription helpers (Stripe temporarily disabled)
  const upgradeToPro = async () => {
    console.info('Stripe checkout is disabled. Contact hello@flashcardify.app for Pro.');
  };

  const manageBilling = async () => {
    console.info('Stripe billing portal is disabled.');
  };

  return (
    <UserContext.Provider
      value={{
        user,
        profile,
        theme,
        popupStates,
        popupStatesLoaded,  // Provide this state to the context
        setUser,
        setProfile,
        loading,
        getUser,
        logout,
        updatePopupState,
        startSignUpProcess,
        finishSignUpProcess,
        upgradeToPro,
        manageBilling,
        toggleColorScheme,
        colorScheme,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the UserContext
export const useUser = () => {
  return useContext(UserContext);
};